import { usePeopleManagementApi } from 'infrastructure/api';
import { useDocumentsStorageApi } from 'infrastructure/api';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import employeeDocValidationSchema from './createupdate/employeeDocValidationSchema';
import { ListResponse, LoadingParams } from 'framework/list';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { cloneDeep } from 'lodash';
import { DocumentStorageContextData } from 'framework/documentStorage';
import { RenderDocumentDataTypes, RenderPreviewDocumentDataTypes, TokenDocumentTypes } from 'framework/documentStorage';
import { EmployeeDocumentCommitType, EmployeeDocumentFormShowType, EmployeeDocumentType } from './types';
import {
  normalizeStoragePath,
  normalizeStoragePathWithSlash,
  useDocumentStorageReceiver,
} from 'domain/shared/documentStorage';

const mapEmloyeeDocumentForCommit = (docData: EmployeeDocumentFormShowType): EmployeeDocumentCommitType => {
  const paths = docData?.paths?.length ? docData?.paths : [];

  if (!paths.length) {
    throw new Error('Required fields are missing');
  }

  return {
    employeeAlias: docData?.entityId || docData?.employeeAlias,
    destination: docData?.destination,
    section: docData?.section,
    fileType: docData?.fileType,
    stage: docData?.stage,
    office: docData?.office,
    number: docData?.number,
    category: docData?.category,
    annullingReason: docData?.annullingReason || null,
    scans: docData?.scans,
    paths: [normalizeStoragePathWithSlash(paths[0] || '')],
  };
};

const useEmployeeDocumentsService = (isGenerateDocumentRoute = false): DocumentStorageContextData => {
  const id = useEmployeeId();
  const { sendPostRequest } = usePeopleManagementApi();
  const { sendPostRequest: sendDocumentStoragePostRequest } = useDocumentsStorageApi('/document-storage');
  const { sendPostRequest: sendOperationalAssetsPostRequest } = useDocumentsStorageApi('/operational-assets');

  const searchDocuments = async (requestParams: LoadingParams): Promise<ListResponse<EmployeeDocumentType>> => {
    try {
      const params = cloneDeep(requestParams);
      const searchString = params.filter?.['specification:search']?.eq || '';

      if (searchString && params.filter) {
        delete params.filter?.['specification:search'];
        params.filter.fileName = {
          ct: searchString,
        };
      }

      const request = await sendPostRequest('/documents/search', {
        ...params,
        filter: {
          ...params.filter,
          entityId: {
            eq: id,
          },
          entityName: {
            eq: 'people',
          },
        },
      });

      return request.json();
    } catch {
      return {
        result: [],
        pageNumber: 0,
        totalPages: 0,
      };
    }
  };

  const getDocumentByPaths = useDocumentStorageReceiver(async (paths: string[]): Promise<EmployeeDocumentType[]> => {
    const request = await sendPostRequest('/documents/files', {
      paths,
    });
    const response = await request.json();
    return response.result || [];
  });

  const uploadDocument = async (data: FormFieldsData) => {
    const request = await sendPostRequest(
      '/document/commit',
      mapEmloyeeDocumentForCommit(data as EmployeeDocumentFormShowType),
    );
    return request.json();
  };

  const editDocument = async (data: FormFieldsData) => {
    const editData = mapEmloyeeDocumentForCommit(data as EmployeeDocumentFormShowType);
    await sendPostRequest('/document/commit', editData);
    return editData; // To update
  };

  const getDocumentTemplateConfig = async (templatePath: string, entityIdentifiers: Record<any, any>) => {
    const request = await sendPostRequest('/template/config', {
      templatePath,
      entityIdentifiers,
    });
    return request.json();
  };

  const getDocumentToken = async (path: string) => {
    const sendTokenRequest = isGenerateDocumentRoute ? sendDocumentStoragePostRequest : sendPostRequest;
    const endpoint = isGenerateDocumentRoute ? '/download/link' : '/document/token';
    const request = await sendTokenRequest(endpoint, {
      path: normalizeStoragePath(path),
    });
    const response = (await request.json()) as TokenDocumentTypes;
    return response.metadata.preSignedUrl;
  };

  const renderDocumentTemplate = async (data: RenderDocumentDataTypes) => {
    const request = await sendPostRequest('/template/render', {
      ...data,
      employeeAlias: id,
    });
    return request.json();
  };

  const renderPreviewDocumentTemplate = async (data: RenderPreviewDocumentDataTypes) => {
    const request = await sendOperationalAssetsPostRequest('/preview-render', {
      ...data,
      employeeAlias: id,
    });

    return request.json();
  };

  const getValidationSchema = () => Promise.resolve(employeeDocValidationSchema);

  return {
    getDocumentByPaths,
    uploadDocument,
    editDocument,
    searchDocuments,
    getDocumentTemplateConfig,
    getValidationSchema,
    getDocumentToken,
    renderDocumentTemplate,
    renderPreviewDocumentTemplate,
  };
};

export default useEmployeeDocumentsService;
